import React, { memo } from 'react'
import { Box, Flex, Text } from '@chakra-ui/core'
import { css } from '@emotion/core'
import { withAlpha } from 'utils'
import LocaleText from 'components/LocaleText'

export const ViewChip = memo<{
  label: string
  color: string
  count?: number
  isActive?: boolean
  onClick?: () => void
}>(({ label, color, count, isActive = true, onClick }) => (
  <Flex
    as={onClick ? 'button' : undefined}
    align="center"
    flexShrink={0}
    bg={isActive ? withAlpha(color, 0.086) : '#FFFFFF'}
    boxShadow={isActive ? `inset 0 0 0 1px ${withAlpha(color, 0.2)}` : '0 1px 2px rgba(15,23,42,0.04)'}
    cursor={onClick ? 'pointer' : 'default'}
    borderRadius="8px"
    px="9px"
    py="3px"
    transition="all 0.15s ease"
    style={{ gap: '6px', outline: 'none', border: 'none' }}
    onClick={onClick}
  >
    <Text fontSize="11px" fontWeight={isActive ? '700' : '600'} color={color} whiteSpace="nowrap">
      <LocaleText text={label} />
    </Text>
    {count !== undefined && (
      <Box minW="18px" textAlign="center">
        <Text fontSize="10px" fontWeight="700" color={color} lineHeight="1.6">
          {count}
        </Text>
      </Box>
    )}
  </Flex>
))

export const KpiCard = memo<{ title: string; value: number; color: string; icon: React.ReactNode }>(
  ({ title, value, color, icon }) => (
    <Flex data-kpi-card bg={`${color}14`} borderRadius="8px" p="8px" align="center" style={{ gap: '10px' }}>
      <Flex
        data-kpi-icon
        w="38px"
        h="38px"
        borderRadius="8px"
        align="center"
        justify="center"
        bg={`${color}2E`}
        color={color}
        flexShrink={0}
      >
        {icon}
      </Flex>
      <Box minW={0}>
        <Text data-kpi-title fontSize="11px" color={color} fontWeight="600" isTruncated opacity={0.75}>
          {title}
        </Text>
        <Text
          data-kpi-value
          fontSize="24px"
          lineHeight="1.1"
          color={color}
          fontWeight="800"
          style={{ filter: 'brightness(0.65)' }}
        >
          {value}
        </Text>
      </Box>
    </Flex>
  )
)

export const TaskTimeChip = memo<{ icon: React.ReactNode; label: string; value: string; color: string }>(
  ({ icon, label, value, color }) => (
    <Flex
      align="center"
      bg="rgba(248,250,252,0.78)"
      border="1px solid rgba(148,163,184,0.28)"
      borderRadius="6px"
      px="8px"
      py="3px"
      minW={0}
      color={color}
      fontSize="10px"
      fontWeight="700"
      lineHeight="1.2"
      style={{ gap: '5px' }}
    >
      {icon}
      <span style={{ opacity: 0.72 }}>{label}</span>
      <span>{value}</span>
    </Flex>
  )
)

export const SkeletonBlock = memo<{ h: string; w?: string; borderRadius?: string; mb?: string; mt?: string }>(
  ({ h, w = '100%', borderRadius = '8px', mb, mt }) => (
    <Box
      h={h}
      w={w}
      borderRadius={borderRadius}
      mb={mb}
      mt={mt}
      bg="#E2E8F0"
      overflow="hidden"
      css={css`
        position: relative;
        &::after {
          content: '';
          position: absolute;
          inset: 0;
          transform: translateX(-100%);
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.72), transparent);
          animation: dashboard-shimmer 1.35s infinite;
        }

        @keyframes dashboard-shimmer {
          100% {
            transform: translateX(100%);
          }
        }
      `}
    />
  )
)
